<template>
  <div class="content-wrapper">
  <div class="branch-dashboard">

    <!-- ===== Header ===== -->
    <div class="d-flex justify-content-between align-items-start mb-2 flex-wrap" style="gap: 12px">
      <div>
        <!-- Reached from the clinic overview (central drill-down) — offer a way back. -->
        <b-link
          v-if="isCentralFlow"
          class="d-inline-flex align-items-center small mb-1"
          @click="goBackToClinic"
        >
          <i class="fas fa-chevron-left mr-1"></i> กลับไปภาพรวมคลินิก
        </b-link>
        <div class="font-weight-bold">
          แดชบอร์ดประจำสาขา · {{ branchName }}
        </div>
        <small class="text-muted">
          <template v-if="d">
            ข้อมูล <b class="text-dark">{{ rangeLabel }}</b>
            <span v-if="comparisonLabel">
              · เทียบกับ <span class="text-primary-dark">{{ comparisonLabel }}</span>
            </span>
          </template>
        </small>
      </div>

      <!-- ===== Range picker + comparison toggle ===== -->
      <div class="d-flex align-items-end flex-wrap" style="gap: 10px">
        <!-- View type: month / year -->
        <div class="bd-seg">
          <button
            type="button"
            class="bd-seg-btn"
            :class="{ on: viewType === 'month' }"
            :disabled="loading"
            @click="setViewType('month')"
          >รายเดือน</button>
          <button
            type="button"
            class="bd-seg-btn"
            :class="{ on: viewType === 'year' }"
            :disabled="loading"
            @click="setViewType('year')"
          >รายปี</button>
        </div>

        <!-- Period select -->
        <b-form-select
          v-if="viewType === 'month'"
          v-model="selectedMonth"
          :options="monthOptions"
          size="sm"
          class="bd-period-select"
          :disabled="loading"
        ></b-form-select>
        <b-form-select
          v-else
          v-model="selectedYear"
          :options="yearOptions"
          size="sm"
          class="bd-period-select"
          :disabled="loading"
        ></b-form-select>

        <!-- Comparison toggle (client-side only) -->
        <div class="d-flex align-items-center" style="gap: 6px">
          <span class="small text-muted font-weight-semibold">เทียบ</span>
          <div class="bd-seg bd-seg--ghost">
            <button
              type="button"
              class="bd-seg-btn"
              :class="{ on: comparison === 'prevPeriod' }"
              @click="comparison = 'prevPeriod'"
            >ช่วงก่อน</button>
            <button
              type="button"
              class="bd-seg-btn"
              :class="{ on: comparison === 'prevYear' }"
              @click="comparison = 'prevYear'"
            >ปีก่อน</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading && !d" class="text-center p-5">
      <b-spinner />
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="text-center p-5">
      <p class="text-danger">โหลดข้อมูลไม่สำเร็จ</p>
      <b-button size="sm" variant="outline-primary" @click="load">ลองใหม่</b-button>
    </div>

    <template v-else-if="d">

      <!-- ===== ROW 1: Hero — รายได้ + ฿/ใบเสร็จ + อันดับ/ค่าเฉลี่ยคลินิก ===== -->
      <b-row class="mb-2">
        <!-- รายได้รวมในช่วง (feature) -->
        <b-col md="5" class="mb-2 mb-md-0">
          <b-card class="hero-card hero-card--feat h-100" body-class="d-flex flex-column justify-content-center">
            <div class="label-feat small font-weight-semibold text-uppercase">รายได้รวมในช่วง</div>
            <div class="d-flex align-items-baseline mt-1" style="gap: 6px">
              <span class="hero-num-lg">{{ formatNumber(d.hero.revenue.value) }}</span>
              <span class="unit-baht">฿</span>
            </div>
            <div class="label-feat small mt-2">
              เงินเก็บจริง · {{ formatNumber(d.stats.receiptsIssued) }} ใบเสร็จ
            </div>
            <div class="badges mt-3">
              <span
                v-for="(b, i) in revenueBadges"
                :key="i"
                class="hero-badge"
                :class="{ active: b.active }"
              >
                <span class="hero-badge-v" :class="b.pct >= 0 ? 'up' : 'down'">
                  {{ b.pct >= 0 ? '▲ +' : '▼ ' }}{{ Math.abs(b.pct).toFixed(1) }}%
                </span>
                <span class="hero-badge-tag">{{ b.tag }}</span>
              </span>
            </div>
          </b-card>
        </b-col>

        <!-- ฿ ต่อใบเสร็จ -->
        <b-col md="4" class="mb-2 mb-md-0">
          <b-card class="hero-card h-100" body-class="d-flex flex-column justify-content-center">
            <div class="label-muted small font-weight-semibold text-uppercase">฿ ต่อใบเสร็จ</div>
            <div class="d-flex align-items-baseline mt-1" style="gap: 4px">
              <span class="hero-num">{{ formatNumber(d.hero.perCase.value) }}</span>
              <span class="small text-muted">฿</span>
            </div>
            <div class="label-muted small mt-2">รายได้ ÷ ใบเสร็จ</div>
            <div class="badges mt-3">
              <span
                v-for="(b, i) in perCaseBadges"
                :key="i"
                class="hero-badge hero-badge--plain"
                :class="{ active: b.active }"
              >
                <span class="hero-badge-v" :class="b.pct >= 0 ? 'up' : 'down'">
                  {{ b.pct >= 0 ? '▲ +' : '▼ ' }}{{ Math.abs(b.pct).toFixed(1) }}%
                </span>
                <span class="hero-badge-tag">{{ b.tag }}</span>
              </span>
            </div>
          </b-card>
        </b-col>

        <!-- อันดับในเครือ + ค่าเฉลี่ยคลินิก -->
        <b-col md="3">
          <b-card class="hero-card h-100" body-class="d-flex flex-column justify-content-center">
            <div class="label-muted small font-weight-semibold text-uppercase">อันดับในเครือ</div>
            <div class="d-flex align-items-center mt-2" style="gap: 14px">
              <div class="rank-ring" v-if="d.rank">
                <svg viewBox="0 0 84 84" width="74" height="74">
                  <circle cx="42" cy="42" r="36" fill="none" stroke="#eef1f6" stroke-width="9" />
                  <circle
                    cx="42" cy="42" r="36" fill="none" stroke="#2196F3" stroke-width="9"
                    stroke-linecap="round"
                    :stroke-dasharray="rankCircumference"
                    :stroke-dashoffset="rankDashOffset"
                  />
                </svg>
                <div class="rank-ctr">
                  <b>#{{ d.rank.rank }}</b>
                  <small>จาก {{ d.rank.of }}</small>
                </div>
              </div>
              <div class="clinic-avg" v-if="d.clinicAvg">
                <div class="ca-item">
                  No-show
                  <b>{{ formatPct(d.clinicAvg.noShowPct) }}</b>
                  <span
                    v-if="noShowVsClinicLabel"
                    class="ca-me"
                    :class="noShowVsClinicLabel.cls"
                  >
                    {{ noShowVsClinicLabel.text }}
                  </span>
                </div>
                <div class="ca-item">
                  ใช้ยูนิต
                  <b>{{ formatPct(d.clinicAvg.utilPct) }}</b>
                  <span
                    v-if="utilVsClinicLabel"
                    class="ca-me"
                    :class="utilVsClinicLabel.cls"
                  >
                    {{ utilVsClinicLabel.text }}
                  </span>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- ===== ROW 2: Stats strip — 5 ช่อง ===== -->
      <b-card class="stat-card mb-2" body-class="p-0">
        <div class="d-flex divide-cols flex-wrap">
          <!-- ผู้ป่วย -->
          <div class="stat-cell flex-fill d-flex flex-column justify-content-center px-3 py-2">
            <span class="label-muted small font-weight-semibold text-uppercase">ผู้ป่วย</span>
            <div class="d-flex align-items-baseline mt-1" style="gap: 4px">
              <span class="stat-num">{{ formatNumber(d.stats.patients.total) }}</span>
              <span class="small text-muted">คน</span>
            </div>
            <trend-badge class="mt-1" :pct="d.stats.patientsPrevPct" />
          </div>

          <!-- ผู้ป่วยใหม่ -->
          <div class="stat-cell flex-fill d-flex flex-column justify-content-center px-3 py-2">
            <span class="label-muted small font-weight-semibold text-uppercase">ผู้ป่วยใหม่</span>
            <div class="d-flex align-items-baseline mt-1" style="gap: 4px">
              <span class="stat-num text-green">{{ formatNumber(d.stats.patients.new) }}</span>
              <span class="small text-muted">คน</span>
            </div>
            <div class="small text-muted mt-1">
              เก่า {{ formatNumber(d.stats.patients.total - d.stats.patients.new) }}
            </div>
          </div>

          <!-- ใบเสร็จ -->
          <div class="stat-cell flex-fill d-flex flex-column justify-content-center px-3 py-2">
            <span class="label-muted small font-weight-semibold text-uppercase">ใบเสร็จ</span>
            <div class="d-flex align-items-baseline mt-1" style="gap: 4px">
              <span class="stat-num">{{ formatNumber(d.stats.receiptsIssued) }}</span>
            </div>
            <trend-badge class="mt-1" :pct="d.stats.receiptsPrevPct" />
          </div>

          <!-- No-show -->
          <div class="stat-cell flex-fill d-flex flex-column justify-content-center px-3 py-2">
            <span class="label-muted small font-weight-semibold text-uppercase">No-show</span>
            <div class="d-flex align-items-baseline mt-1" style="gap: 4px">
              <span class="stat-num">{{ formatNumber(d.stats.noShow.count) }}</span>
              <span v-if="d.stats.noShow.pct != null" class="small text-muted">/ {{ d.stats.noShow.pct.toFixed(1) }}%</span>
            </div>
            <trend-badge
              v-if="d.stats.noShowPrevPct != null"
              class="mt-1"
              :pct="d.stats.noShowPrevPct"
            />
          </div>

          <!-- ใช้ยูนิตเฉลี่ย -->
          <div class="stat-cell flex-fill d-flex flex-column justify-content-center px-3 py-2">
            <span class="label-muted small font-weight-semibold text-uppercase">ใช้ยูนิตเฉลี่ย</span>
            <div class="d-flex align-items-baseline mt-1" style="gap: 4px">
              <span class="stat-num">{{ unitAvgPct }}</span>
              <span class="small text-muted">%</span>
            </div>
            <div class="small text-muted mt-1" v-if="d.clinicAvg && d.clinicAvg.utilPct != null">
              เฉลี่ยคลินิก {{ d.clinicAvg.utilPct }}%
            </div>
          </div>
        </div>
      </b-card>

      <!-- ===== ROW 3: BarChart ยูนิตรายชั่วโมง ===== -->
      <b-card
        v-if="d.hourlyUnits && d.hourlyUnits.unitCount"
        class="unit-card mb-2"
        body-class="d-flex flex-column p-3"
      >
        <div class="d-flex justify-content-between align-items-start mb-1">
          <div>
            <div class="font-weight-bold small">
              % การใช้ยูนิตรายชั่วโมง
              <span class="label-muted font-weight-normal">· เฉลี่ยทุกวันในช่วง · {{ d.hourlyUnits.unitCount }} ยูนิต</span>
            </div>
            <div class="label-muted small">
              เห็นว่าช่วงเวลาไหนเต็ม/ว่าง เพื่อจัดคิวนัด
            </div>
          </div>
          <div class="d-flex align-items-center small text-muted" style="gap: 10px">
            <span class="d-flex align-items-center" style="gap: 4px">
              <span class="legend-dot" style="background: #2196F3"></span>≥75% (เต็ม)
            </span>
            <span class="d-flex align-items-center" style="gap: 4px">
              <span class="legend-dot" style="background: #9bbcff"></span>50–74%
            </span>
            <span class="d-flex align-items-center" style="gap: 4px">
              <span class="legend-dot" style="background: #E4E9F1"></span>&lt;50% (ว่าง)
            </span>
          </div>
        </div>
        <!-- :styles sets the height on vue-chartjs's own wrapper div; without it
             the canvas has no fixed parent height and balloons past the box,
             clipping the bars + x-axis (maintainAspectRatio:false). -->
        <bar-chart
          :chart-data="unitChartData"
          :options="unitChartOptions"
          :styles="{ position: 'relative', height: '200px' }"
        />
      </b-card>

      <!-- ===== ROW 4: รายได้หมอ + Case Mix ===== -->
      <b-row class="mb-2">
        <!-- รายได้ต่อหมอ -->
        <b-col md="6" class="mb-2 mb-md-0">
          <b-card class="h-100" body-class="d-flex flex-column p-3">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div class="font-weight-bold small">รายได้ต่อหมอ <span class="label-muted font-weight-normal">· ในช่วง</span></div>
                <div class="label-muted small">เรียงตามรายได้ · % = สัดส่วนของสาขา</div>
              </div>
              <span class="label-muted small" v-if="sortedDoctors.length">
                {{ sortedDoctors.length }} ท่าน
              </span>
            </div>
            <div
              v-if="sortedDoctors.length"
              class="d-flex flex-column"
              style="gap: 8px; flex: 1"
            >
              <div v-for="(doc, i) in sortedDoctors" :key="doc.doctorId || i">
                <div class="d-flex justify-content-between align-items-baseline mb-1">
                  <span class="small font-weight-500">{{ doc.name }}</span>
                  <div class="d-flex align-items-baseline" style="gap: 8px">
                    <span class="small text-muted">{{ formatNumber(doc.cases) }} เคส</span>
                    <span class="small font-weight-bold">{{ formatNumber(doc.revenue) }} ฿</span>
                    <span class="small font-weight-bold text-primary-dark" style="min-width: 34px; text-align: right">
                      {{ doctorShare(doc.revenue) }}%
                    </span>
                  </div>
                </div>
                <div class="doctor-bar-track">
                  <div
                    class="doctor-bar-fill"
                    :style="{ width: doctorBarWidth(doc.revenue) + '%' }"
                  ></div>
                </div>
              </div>
            </div>
            <div v-else class="text-muted small mt-2">ยังไม่มีข้อมูลทันตแพทย์ในช่วงนี้</div>
          </b-card>
        </b-col>

        <!-- Case Mix Donut -->
        <b-col md="6">
          <b-card class="h-100" body-class="d-flex flex-column p-3">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div>
                <div class="font-weight-bold small">สัดส่วนหัตถการ <span class="label-muted font-weight-normal">· ตามหมวด</span></div>
                <div class="label-muted small">สัดส่วนรายได้ตามหมวด</div>
              </div>
            </div>
            <div
              v-if="caseMixItems && caseMixItems.length"
              class="d-flex align-items-center"
              style="gap: 12px; flex: 1; min-height: 0"
            >
              <div style="position: relative; height: 150px; width: 150px; flex: 0 0 150px; max-width: 150px">
                <pie-chart :chart-data="caseMixChartData" :options="caseMixOptions" />
              </div>
              <div class="flex-fill" style="min-width: 0">
                <div
                  v-for="(item, i) in caseMixItems"
                  :key="i"
                  class="d-flex justify-content-between align-items-center py-1"
                >
                  <span class="d-flex align-items-center small" style="gap: 6px; min-width: 0">
                    <span class="legend-dot" :style="{ background: item.color }"></span>
                    <span class="text-truncate">{{ item.category }}</span>
                  </span>
                  <span class="small font-weight-bold flex-shrink-0">{{ item.pct }}%</span>
                </div>
              </div>
            </div>
            <div v-else class="text-muted small mt-2">ยังไม่มีข้อมูล case mix ในช่วงนี้</div>
          </b-card>
        </b-col>
      </b-row>

      <!-- ===== ROW 5: รายการรักษาทำเงินสูงสุด ===== -->
      <b-card class="mb-2" body-class="p-3">
        <div class="font-weight-bold small mb-2">
          รายการรักษาทำเงินสูงสุด
          <span class="label-muted font-weight-normal">· Top {{ (d.topTreatments || []).length }}</span>
        </div>
        <div
          v-if="d.topTreatments && d.topTreatments.length"
          class="d-flex flex-column"
          style="gap: 11px"
        >
          <div
            v-for="(t, i) in d.topTreatments"
            :key="t.treatmentId || i"
            class="treat-row"
          >
            <div class="treat-rank">{{ i + 1 }}</div>
            <div style="min-width: 0">
              <div class="d-flex justify-content-between align-items-baseline small mb-1" style="gap: 10px">
                <span class="text-truncate">{{ t.name }}</span>
                <span class="font-weight-bold flex-shrink-0">
                  ฿{{ formatNumber(t.revenue) }}
                  <span class="text-muted font-weight-normal" style="font-size: 11.5px">{{ formatNumber(t.count) }} เคส</span>
                </span>
              </div>
              <div class="treat-track">
                <div class="treat-fill" :style="{ width: treatBarWidth(t.revenue) + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-muted small text-center py-4">ไม่มีข้อมูลรายการรักษา</div>
      </b-card>

      <!-- branchAnalysis is gated to branch roles; in the central drill-down
           (clinic-level viewers) that route would reject, so hide the link there -->
      <div class="text-right mt-2" v-if="!isCentralFlow">
        <b-link @click="goAnalysis">ดูบทวิเคราะห์เชิงลึก + AI ▸</b-link>
      </div>

    </template>
  </div>
  </div>
</template>

<script>
import moment from "moment";
import { formatNumber } from "@/utils/utils";
import BarChart from "@/components/chart/BarChart.vue";
import PieChart from "@/components/chart/PieChart.vue";
import TrendBadge from "@/components/dashboard/TrendBadge.vue";

const CASE_MIX_COLORS = [
  "#2196F3",
  "#26C6DA",
  "#8B5CF6",
  "#F59E0B",
  "#EF4444",
  "#94A3B8",
  "#22C55E",
  "#F97316",
  "#EC4899",
  "#64748B",
];

export default {
  name: "BranchDashboard",
  components: { BarChart, PieChart, TrendBadge },

  data() {
    return {
      d: null,
      error: null,
      loading: false,

      // Range picker (mirrors ClinicDashboard.vue)
      viewType: "month",
      selectedMonth: moment().format("YYYY-MM"),
      selectedYear: moment().format("YYYY"),
      monthOptions: this.buildMonthOptions(),
      yearOptions: this.buildYearOptions(),

      // Comparison toggle: 'prevPeriod' | 'prevYear' — CLIENT-SIDE only (both pcts in payload)
      comparison: "prevPeriod",
    };
  },

  computed: {
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
    isCentralFlow() {
      return this.$route.name === "CentralBranchDashboard";
    },
    branchName() {
      // In the central drill-down, getBranchInfo reflects the logged-in branch,
      // not the route's branch — prefer a name passed through navigation.
      if (this.isCentralFlow) {
        return this.$route.params.branchName || this.branchUrl;
      }
      const info = this.$store.getters["moduleBranch/getBranchInfo"];
      return (info && info.name) || this.branchUrl;
    },

    // ---- Date range (mirrors ClinicDashboard.vue) ----
    startDt() {
      if (this.viewType === "year") {
        return moment(this.selectedYear).startOf("year").format();
      }
      return this.selectedMonth
        ? moment(this.selectedMonth).startOf("month").format()
        : "";
    },
    endDt() {
      if (this.viewType === "year") {
        return moment(this.selectedYear).endOf("year").format();
      }
      return this.selectedMonth
        ? moment(this.selectedMonth).endOf("month").format()
        : "";
    },

    // ---- Range / comparison labels ----
    rangeLabel() {
      const r = this.d && this.d.range;
      const start = r && r.startDt ? r.startDt : this.startDt;
      const end = r && r.endDt ? r.endDt : this.endDt;
      return this.formatRange(start, end);
    },
    comparisonLabel() {
      if (!this.d || !this.d.comparison) return "";
      const cmp = this.d.comparison[this.comparison];
      if (!cmp) return "";
      const suffix = this.comparison === "prevYear" ? " (ปีก่อน)" : " (ช่วงก่อนหน้า)";
      return this.formatRange(cmp.startDt, cmp.endDt) + suffix;
    },
    comparisonTag() {
      return this.comparison === "prevYear" ? "vs ปีก่อน" : "vs ช่วงก่อน";
    },

    // ---- Hero comparison badges (active comparison first, mirrors mockup) ----
    revenueBadges() {
      return this.buildHeroBadges(this.d && this.d.hero && this.d.hero.revenue);
    },
    perCaseBadges() {
      return this.buildHeroBadges(this.d && this.d.hero && this.d.hero.perCase);
    },

    // ---- Rank ring geometry ----
    rankCircumference() {
      return 2 * Math.PI * 36;
    },
    rankDashOffset() {
      // Fill proportional to rank position: #1 of N => fullest ring.
      const r = this.d && this.d.rank;
      if (!r || !r.of) return this.rankCircumference;
      const filled = (r.of - r.rank + 1) / r.of;
      return this.rankCircumference * (1 - filled);
    },

    // ---- No-show / util vs clinic average (percentage-point diff) ----
    noShowVsClinicPct() {
      if (!this.d || !this.d.stats || !this.d.clinicAvg) return null;
      const p = this.d.stats.noShow && this.d.stats.noShow.pct;
      const avg = this.d.clinicAvg.noShowPct;
      if (p == null || avg == null) return null;
      return p - avg;
    },
    utilVsClinicPct() {
      if (!this.d || !this.d.clinicAvg) return null;
      const me = this.unitAvgPct;
      const avg = this.d.clinicAvg.utilPct;
      if (me == null || avg == null) return null;
      return me - avg;
    },
    // Pre-computed labels (avoid bare `<`/`<=` operators in template mustaches,
    // which the Vue template compiler mis-parses as opening tags).
    noShowVsClinicLabel() {
      const diff = this.noShowVsClinicPct;
      if (diff == null) return null;
      const better = diff <= 0; // lower no-show is better
      return {
        cls: better ? "up" : "down",
        text:
          (better ? "ดีกว่าเฉลี่ย " : "สูงกว่าเฉลี่ย +") +
          Math.abs(diff).toFixed(1),
      };
    },
    utilVsClinicLabel() {
      const diff = this.utilVsClinicPct;
      if (diff == null) return null;
      const higher = diff >= 0; // higher utilisation is better
      return {
        cls: higher ? "up" : "down",
        text:
          (higher ? "สูงกว่าเฉลี่ย +" : "ต่ำกว่าเฉลี่ย ") +
          Math.abs(diff).toFixed(0),
      };
    },

    // ---- Hourly units (avg occupancy across all days in range) ----
    unitAvgPct() {
      const h = this.d && this.d.hourlyUnits;
      if (!h || !h.hours || !h.hours.length) return 0;
      const sum = h.hours.reduce((s, x) => s + (x.avgOccupancyPct || 0), 0);
      return Math.round(sum / h.hours.length);
    },
    unitChartData() {
      const h = this.d && this.d.hourlyUnits;
      if (!h || !h.hours) return { labels: [], datasets: [] };
      return {
        labels: h.hours.map((x) => String(x.hour).padStart(2, "0") + ":00"),
        datasets: [
          {
            label: "การใช้งาน",
            data: h.hours.map((x) => x.avgOccupancyPct),
            backgroundColor: h.hours.map((x) => {
              const v = x.avgOccupancyPct || 0;
              return v >= 75 ? "#2196F3" : v >= 50 ? "#9bbcff" : "#E4E9F1";
            }),
            borderRadius: 4,
          },
        ],
      };
    },
    unitChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: (tooltipItem) => "ใช้งาน " + tooltipItem.yLabel + "%",
          },
        },
        scales: {
          xAxes: [
            {
              gridLines: { display: false },
              ticks: { fontColor: "#6B7280", fontSize: 10 },
            },
          ],
          yAxes: [
            {
              ticks: {
                min: 0,
                max: 100,
                stepSize: 25,
                fontColor: "#6B7280",
                fontSize: 10,
                callback: (v) => v + "%",
              },
              gridLines: { color: "#E5EAF0" },
              scaleLabel: {
                display: true,
                labelString: "% การใช้งาน",
                fontColor: "#6B7280",
                fontSize: 10,
              },
            },
          ],
        },
      };
    },

    // ---- Doctors sorted by revenue descending ----
    sortedDoctors() {
      if (!this.d || !this.d.doctors) return [];
      return [...this.d.doctors].sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
    },
    maxDoctorRevenue() {
      if (!this.sortedDoctors.length) return 1;
      return this.sortedDoctors[0].revenue || 1;
    },
    totalDoctorRevenue() {
      return this.sortedDoctors.reduce((s, doc) => s + (doc.revenue || 0), 0);
    },

    // ---- Case mix (donut) ----
    caseMixItems() {
      if (!this.d || !this.d.caseMix || !this.d.caseMix.length) return [];
      return this.d.caseMix.map((item, i) => ({
        ...item,
        color: item.color || CASE_MIX_COLORS[i % CASE_MIX_COLORS.length],
      }));
    },
    caseMixChartData() {
      const items = this.caseMixItems;
      return {
        labels: items.map((c) => c.category),
        datasets: [
          {
            data: items.map((c) => c.pct),
            backgroundColor: items.map((c) => c.color),
            borderWidth: 2,
            borderColor: "#fff",
          },
        ],
      };
    },
    caseMixOptions() {
      return {
        responsive: true,
        // Keep the donut square and sized by width only. With maintainAspectRatio
        // false the canvas tracks its (flex-fill) parent's height and balloons
        // past the 150px box, drifting below the legend.
        maintainAspectRatio: true,
        aspectRatio: 1,
        cutoutPercentage: 58,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.labels[tooltipItem.index] || "";
              const val = data.datasets[0].data[tooltipItem.index];
              return label + ": " + val + "%";
            },
          },
        },
      };
    },

    // ---- Top treatments bar max ----
    maxTreatmentRevenue() {
      if (!this.d || !this.d.topTreatments || !this.d.topTreatments.length) return 1;
      return Math.max(...this.d.topTreatments.map((t) => t.revenue || 0)) || 1;
    },
  },

  methods: {
    // ---- Picker option builders ----
    buildMonthOptions() {
      moment.locale("th");
      const options = [];
      for (let i = 0; i < 24; i++) {
        const date = moment().subtract(i, "months");
        options.push({
          value: date.format("YYYY-MM"),
          text: date.format("MMMM YYYY"),
        });
      }
      return options;
    },
    buildYearOptions() {
      const years = [];
      const currentYear = moment().year();
      for (let i = 0; i < 3; i++) {
        const year = currentYear - i;
        years.push({ value: year.toString(), text: `ปี ${year + 543}` });
      }
      return years;
    },

    setViewType(type) {
      if (this.viewType === type) return;
      this.viewType = type;
      // Reset selection to current period for the new view type
      if (type === "month") {
        this.selectedMonth = moment().format("YYYY-MM");
      } else {
        this.selectedYear = moment().format("YYYY");
      }
      // startDt/endDt watcher triggers the refetch
    },

    // active comparison first, both badges shown (mirrors mockup badges())
    buildHeroBadges(metric) {
      if (!metric) return [];
      const order =
        this.comparison === "prevPeriod"
          ? ["prevPeriod", "prevYear"]
          : ["prevYear", "prevPeriod"];
      const tags = { prevPeriod: "vs ช่วงก่อน", prevYear: "vs ปีก่อน" };
      return order
        .filter((k) => Number.isFinite(metric[k + "Pct"]))
        .map((k, i) => ({
          pct: metric[k + "Pct"],
          tag: tags[k],
          active: i === 0,
        }));
    },

    async load() {
      this.error = null;
      this.loading = true;
      try {
        this.d = await this.$store.dispatch("dashboard/fetchBranchDashboard", {
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          startDt: this.startDt,
          endDt: this.endDt,
        });
      } catch (e) {
        this.error = e;
        this.$bvToast.toast("โหลดข้อมูลแดชบอร์ดไม่สำเร็จ", {
          variant: "danger",
          title: "ข้อผิดพลาด",
          autoHideDelay: 4000,
        });
      } finally {
        this.loading = false;
      }
    },

    goBackToClinic() {
      // Central drill-down always originates from the clinic overview, so return
      // there (works on hard refresh too, unlike router.back()).
      this.$router.push({
        name: "ClinicDashboard",
        params: { clinicUrl: this.clinicUrl },
      });
    },

    goAnalysis() {
      // branchAnalysis is a branch-mode route; navigate to it directly regardless
      // of whether we are in central or branch mode — both routes carry clinicUrl
      // and branchUrl params.  Note: if arriving via CentralBranchDashboard the
      // router guard will trigger requiresDataFetch for branchAnalysis; the guard
      // handles this transparently via the data-fetch path.
      this.$router.push({
        name: "branchAnalysis",
        params: { clinicUrl: this.clinicUrl, branchUrl: this.branchUrl },
      });
    },

    // ---- Bar widths / shares ----
    doctorShare(revenue) {
      if (!this.totalDoctorRevenue) return 0;
      return Math.round((revenue / this.totalDoctorRevenue) * 100);
    },
    doctorBarWidth(revenue) {
      if (!this.maxDoctorRevenue) return 0;
      return Math.round((revenue / this.maxDoctorRevenue) * 100);
    },
    treatBarWidth(revenue) {
      return Math.round(((revenue || 0) / this.maxTreatmentRevenue) * 100);
    },

    // ---- Formatting ----
    formatNumber(val) {
      return formatNumber(val || 0);
    },
    formatPct(val) {
      return val == null ? "–" : val.toFixed(1) + "%";
    },
    formatRange(startDt, endDt) {
      if (!startDt) return "";
      const start = moment(startDt);
      const end = endDt ? moment(endDt) : null;
      if (
        this.viewType === "year" ||
        (end && start.year() === end.year() && start.month() === 0 && end.month() === 11)
      ) {
        return "ปี " + (start.year() + 543);
      }
      if (end && start.isSame(end, "month")) {
        return start.locale("th").format("MMMM YYYY");
      }
      if (end) {
        return start.locale("th").format("D MMM") + " – " + end.locale("th").format("D MMM YYYY");
      }
      return start.locale("th").format("MMMM YYYY");
    },
  },

  created() {
    this.load();
  },

  watch: {
    // Re-fetch whenever the selected range changes (picker or view-type change).
    startDt() {
      this.load();
    },
    endDt() {
      this.load();
    },

    // Vue Router reuses this instance when only :branchUrl changes — refetch.
    branchUrl() {
      this.d = null;
      this.load();
    },
  },
};
</script>

<style scoped>
/* ---- Brand tokens ---- */
.branch-dashboard {
  --bd-primary: #2196f3;
  --bd-primary-dark: #1976d2;
  --bd-primary-light: #e3f2fd;
  --bd-green: #22c55e;
  --bd-red: #ef4444;
  --bd-orange: #f59e0b;
  --bd-purple: #8b5cf6;
  --bd-bg: #f5f8fb;
  --bd-border: #e5eaf0;
  --bd-text-muted: #6b7280;
}

/* ---- Utilities ---- */
.text-green  { color: var(--bd-green)  !important; }
.text-red    { color: var(--bd-red)    !important; }
.text-primary-dark { color: var(--bd-primary-dark) !important; }
.font-weight-500 { font-weight: 500; }
.font-weight-semibold { font-weight: 600; }
.label-muted { color: var(--bd-text-muted); }

/* ---- Range picker (segmented control) ---- */
.bd-seg {
  display: inline-flex;
  background: #fff;
  border: 1px solid #d2dae6;
  border-radius: 9px;
  padding: 3px;
}
.bd-seg--ghost {
  border-color: var(--bd-border);
}
.bd-seg-btn {
  font-size: 12px;
  font-weight: 600;
  color: var(--bd-text-muted);
  background: none;
  border: 0;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.15s;
}
.bd-seg-btn:disabled { cursor: not-allowed; opacity: 0.6; }
.bd-seg-btn.on {
  background: var(--bd-primary);
  color: #fff;
}
.bd-seg--ghost .bd-seg-btn.on {
  background: var(--bd-primary-light);
  color: var(--bd-primary-dark);
}
.bd-period-select {
  width: auto;
  min-width: 150px;
  font-weight: 600;
}

/* ---- Hero cards ---- */
.hero-card {
  border: 1px solid var(--bd-border);
  border-radius: 12px;
}
.hero-card--feat {
  border: 0;
  background: linear-gradient(135deg, #1d3a73, #2a6df4);
  color: #fff;
}
.hero-card--feat .unit-baht,
.hero-card--feat .label-feat { color: #fff; }
.label-feat { color: #dbe6ff; opacity: 0.9; }
.hero-num-lg {
  font-size: clamp(28px, 3.2vw, 42px);
  font-weight: 700;
  line-height: 1;
}
.hero-num {
  font-size: clamp(20px, 2.4vw, 32px);
  font-weight: 700;
  line-height: 1;
}
.unit-baht { font-size: 1.1rem; font-weight: 600; opacity: 0.7; color: var(--bd-text-muted); }

/* ---- Hero comparison badges ---- */
.badges { display: flex; gap: 8px; flex-wrap: wrap; }
.hero-badge {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 9px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #eaf1ff;
}
.hero-badge.active { box-shadow: 0 1px 0 #fff inset; }
.hero-badge--plain {
  background: var(--bd-bg);
  border-color: var(--bd-border);
  color: var(--bd-text-muted);
}
.hero-badge--plain.active { box-shadow: 0 1px 0 var(--bd-primary) inset; }
.hero-badge-v.up { color: #7ff0c4; }
.hero-badge-v.down { color: #ffb3a5; }
.hero-badge--plain .hero-badge-v.up { color: var(--bd-green); }
.hero-badge--plain .hero-badge-v.down { color: var(--bd-red); }
.hero-badge-tag { font-size: 10px; font-weight: 600; opacity: 0.7; }

/* ---- Rank ring ---- */
.rank-ring { position: relative; width: 74px; height: 74px; flex-shrink: 0; }
.rank-ring svg { transform: rotate(-90deg); }
.rank-ctr {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  line-height: 1;
  font-weight: 700;
}
.rank-ctr b { font-size: 22px; }
.rank-ctr small { font-size: 10px; color: var(--bd-text-muted); font-weight: 600; }

/* ---- Clinic-avg comparison ---- */
.clinic-avg { display: flex; flex-direction: column; gap: 8px; }
.ca-item { font-size: 11.5px; color: var(--bd-text-muted); }
.ca-item b { display: block; font-size: 17px; color: #1f2a37; font-weight: 700; margin-top: 1px; }
.ca-me { font-size: 11px; font-weight: 600; }
.ca-me.up { color: var(--bd-green); }
.ca-me.down { color: var(--bd-red); }

/* ---- Stat strip ---- */
.stat-card {
  border: 1px solid var(--bd-border);
  border-radius: 12px;
}
.stat-cell {
  min-width: 0;
}
.divide-cols > * + * {
  border-left: 1px solid var(--bd-border);
}
.stat-num {
  font-size: clamp(17px, 1.9vw, 24px);
  font-weight: 700;
  line-height: 1;
}

/* ---- Unit chart card ---- */
.unit-card {
  border: 1px solid var(--bd-border);
  border-radius: 12px;
}

/* ---- Doctor bar ---- */
.doctor-bar-track {
  position: relative;
  border-radius: 999px;
  background: var(--bd-bg);
  height: 8px;
}
.doctor-bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #2196f3, #5b8dff);
  transition: width 0.4s ease;
}

/* ---- Top treatments ---- */
.treat-row {
  display: grid;
  grid-template-columns: 22px 1fr;
  align-items: center;
  gap: 12px;
}
.treat-rank {
  font-weight: 700;
  color: var(--bd-text-muted);
  font-size: 13px;
  text-align: center;
}
.treat-track {
  height: 7px;
  border-radius: 6px;
  background: var(--bd-bg);
  overflow: hidden;
}
.treat-fill {
  height: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #2196f3, #5b8dff);
  transition: width 0.4s ease;
}

/* ---- Legend dot ---- */
.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}
</style>
